import React, { useEffect, useState } from 'react';
import { Platform, View } from 'react-native';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { getIsMaintenance } from '../../store/selectors/config.selectors';
import { getLoginData } from '../../store/selectors/login.selectors';
import {
	login,
	loginStep2SuccessCallback,
	logout,
	resetLoginError,
} from '../../store/actions/login.actions';
import AlertModal from '../../modals/AlertModal/AlertModal.component';
import I18n from '../../components/shared/i18n/I18n.component';
import { i18nService } from '../../services/i18n.service';
import { Maintenance } from '../SignIn/components/Maintenance';
import ForgotPasswordContainer from '../ForgotPassword/ForgotPassword.container';
import SignInV2Layout from './components/SignInV2Layout';
import { IdStep } from './components/IdStep.component';
import { PasswordStep } from './components/PasswordStep.component';
import { isCodeComplete, OtpStep } from './components/OtpStep.component';
import {
	checkId,
	CheckIdResult,
	isConnectionError,
	OtpChannelType,
	sendOtp,
	verifyOtp,
	verifyPassword,
} from './SignInV2.api';
import { isStepTokenExpired, OtpTarget, saveBrowserCredentials, toOtpTarget,
} from './SignInV2.utils';

/** wrong codes allowed per sent code - after that only a resend or a fresh flow unlocks submit */
const MAX_OTP_ATTEMPTS = 5;

export enum SignInV2Step {
	ID = 'ID',
	PASSWORD = 'PASSWORD',
	OTP = 'OTP',
	FORGOT_PASSWORD = 'FORGOT_PASSWORD',
}

interface SignInV2ContainerProps {
	navigation: any;
}

const SignInV2Container = ({ navigation }: SignInV2ContainerProps) => {
	const dispatch = useDispatch();
	const store = useStore();
	const [step, setStep] = useState<SignInV2Step>(SignInV2Step.ID);
	const [idNumber, setIdNumber] = useState('');
	const [password, setPassword] = useState('');
	const [checkIdResult, setCheckIdResult] = useState<CheckIdResult | null>(
		null,
	);
	const [otpTarget, setOtpTarget] = useState<OtpTarget | null>(null);
	const [code, setCode] = useState('');
	const [stepToken, setStepToken] = useState<string | null>(null);
	const [resendNonce, setResendNonce] = useState(0); // resend otp step nonce, bumped to restart the cooldown
	const [errorKey, setErrorKey] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [otpFailedAttempts, setOtpFailedAttempts] = useState(0);
	const isMaintenance = useSelector(getIsMaintenance);
	const { error, errorMessage } = useSelector(getLoginData);

	// no employer requires 2FA -> offer the one-time-code alternative
	const showOneTimeCode = !checkIdResult?.requires2fa;
	// web is guarded by recaptcha, so the attempts lock is mobile-only
	const otpLocked = Platform.OS !== 'web' && otpFailedAttempts >= MAX_OTP_ATTEMPTS;


	useEffect(() => {
		errorKey && setErrorKey(null);
	}, [idNumber]);

	useEffect(() => {
		error && dispatch(resetLoginError());
		errorKey === 'signInV2.password.emptyPassword' && setErrorKey(null);
	}, [password]);

	useEffect(() => {
		error && setLoading(false);
	}, [error]);

	const onContinueId = async () => {
		if (loading) {
			return;
		}
		if (!idNumber) {
			setErrorKey('signInV2.error.emptyId');
			return;
		}
		setErrorKey(null);
		setLoading(true);
		try {
			const res = await checkId(idNumber);
			if (!res.valid) {
				setErrorKey('signInV2.error.idNotFound');
			} else {
				setCheckIdResult(res);
				setStepToken(res.stepToken ?? null);
				setStep(SignInV2Step.PASSWORD);
			}
		} catch (err) {
			setErrorKey(
				isConnectionError(err)
					? 'errors.noConnection'
					: 'signInV2.error.general',
			);
		} finally {
			setLoading(false);
		}
	};

	const onSubmitPassword = async () => {
		if (loading) {
			return;
		}
		if (!password) {
			setErrorKey('signInV2.password.emptyPassword');
			return;
		}
		setErrorKey(null);

		// no 2FA -> straight through the existing login flow
		if (!checkIdResult?.requires2fa) {
			if (isStepTokenExpired(stepToken)) {
				restartExpiredFlow();
				return;
			}
			setLoading(true);
			await dispatch(
				login({
					login: idNumber,
					password,
					stepToken: stepToken ?? undefined,
				}),
			);
			store.getState().login.loggedIn &&
				saveBrowserCredentials(idNumber, password);
			return;
		}

		setLoading(true);
		try {
			const res = await verifyPassword(idNumber, password, stepToken ?? undefined);
			if (!res.ok) {
				// wrong password mints no new token - the current one stays valid for the retry
				setErrorKey('signInV2.password.passwordError');
			} else {
				goToOtp(res.sentTo, res.stepToken);
			}
		} catch (err) {
			// a wrong password comes back as 200 {ok:false}, so a 400 here is the
			// flow being refused rather than the credentials
			onStepError(err, 'signInV2.password.passwordError');
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Entering the OTP step always means a fresh code, so restart the cooldown.
	 */
	const goToOtp = (sentTo?: OtpChannelType, nextStepToken?: string) => {
		setOtpTarget(sentTo ? toOtpTarget(checkIdResult, sentTo) : null);
		setCode('');
		setErrorKey(null);
		setOtpFailedAttempts(0);
		nextStepToken && setStepToken(nextStepToken);
		setResendNonce(nonce => nonce + 1);
		setStep(SignInV2Step.OTP);
	};

	const restartExpiredFlow = () => {
		onBackToId(); // clears errorKey, so the message below has to come after
		setErrorKey('signInV2.error.sessionExpired');
	};

	const onStepError = (err: any, fallbackKey: string) => {
		if (isConnectionError(err)) {
			setErrorKey('errors.noConnection');
			return;
		}
		if (err?.status === 400 && stepToken) {
			restartExpiredFlow();
			return;
		}
		setErrorKey(fallbackKey);
	};

	// no 2FA: the code was never sent by the password step, so send it now
	const onOneTimeCode = async () => {
		if (loading) {
			return;
		}
		setLoading(true);
		try {
			const res = await sendOtp(idNumber, stepToken ?? undefined);
			goToOtp(res.sentTo, res.stepToken);
		} catch (err) {
			onStepError(err, 'signInV2.error.general');
		} finally {
			setLoading(false);
		}
	};

	const resendCode = async () => {
		if (loading) {
			return;
		}
		setLoading(true);
		try {
			const res = await sendOtp(idNumber, stepToken ?? undefined);
			goToOtp(res.sentTo, res.stepToken);
		} catch (err) {
			onStepError(err, 'signInV2.error.general');
		} finally {
			setLoading(false);
		}
	};

	const onSubmitOtp = async () => {
		if (loading || otpLocked || !isCodeComplete(code)) {
			return;
		}
		setErrorKey(null);
		setLoading(true);
		try {
			const res = await verifyOtp(idNumber, code, stepToken ?? undefined);
			if (!res.ok) {
				// an expired step token is refused with the same bare 400 as a
				// wrong code, so the token's own exp is what tells them apart -
				// otherwise a timed-out flow reads as "the code you typed is wrong"
				if (isStepTokenExpired(stepToken)) {
					restartExpiredFlow();
				} else {
					const attempts = otpFailedAttempts + 1;
					setOtpFailedAttempts(attempts);
					setErrorKey(
						Platform.OS !== 'web' && attempts >= MAX_OTP_ATTEMPTS
							? 'signInV2.otp.tooManyAttempts'
							: 'signInV2.otp.codeError',
					);

				}
			} else if (res.loginPayload) {
				saveBrowserCredentials(idNumber, password);
				dispatch(loginStep2SuccessCallback(res.loginPayload));
			}
		} catch (err) {
			onStepError(err, 'signInV2.error.general');
		} finally {
			setLoading(false);
		}
	};

	// every back in the flow restarts it from the id step
	const onBackToId = () => {
		setPassword('');
		setCode('');
		setOtpTarget(null);
		// the flow starts over, so the proof of the old one is void - continuing
		// from the id step runs check-id again and mints a fresh token
		setStepToken(null);
		setErrorKey(null);
		error && dispatch(resetLoginError());
		setStep(SignInV2Step.ID);
	};

	const onForgotPassword = () => {
		setErrorKey(null);
		setStep(SignInV2Step.FORGOT_PASSWORD);
	};

	// the 2FA branch reports through errorKey, the real login thunk through redux
	const passwordErrorKey =
		errorKey ||
		(error && errorMessage !== 'OUTDATED_APP_VERSION'
			? errorMessage === 'NO_CONNECTION'
				? 'errors.noConnection'
				: 'signInV2.password.passwordError'
			: null);

	const renderStep = () => {
		switch (step) {
			case SignInV2Step.ID:
				return (
					<IdStep
						idNumber={idNumber}
						setIdNumber={setIdNumber}
						onContinue={onContinueId}
						errorKey={errorKey}
						loading={loading}
					/>
				);
			case SignInV2Step.PASSWORD:
				return (
					<PasswordStep
						idNumber={idNumber}
						password={password}
						setPassword={setPassword}
						onSubmit={onSubmitPassword}
						onForgotPassword={onForgotPassword}
						onOneTimeCode={onOneTimeCode}
						onBack={onBackToId}
						showOneTimeCode={showOneTimeCode}
						errorKey={passwordErrorKey}
						loading={loading}
					/>
				);
			case SignInV2Step.OTP:
				return (
					<OtpStep
						otpTarget={otpTarget}
						code={code}
						setCode={setCode}
						onSubmit={onSubmitOtp}
						onResend={resendCode}
						onBack={onBackToId}
						resendNonce={resendNonce}
						failedAttempts={otpFailedAttempts}
						locked={otpLocked}
						errorKey={errorKey}
						loading={loading}
					/>
				);
			default:
				return null;
		}
	};

	// message NO_PERMISSIONS will be provided when the feature_flags array is empty
	if (error && errorMessage === 'NO_PERMISSIONS') {
		return (
			<View
				style={{
					height: '100%',
					width: '100%',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<AlertModal
					submitBtnText={i18nService.translate('general.understood')}
					children={<I18n>error.noPermissions</I18n>}
					iconName='attention'
					onSubmit={() => dispatch(logout())}
				/>
			</View>
		);
	}

	// brings its own card shell, so it replaces the whole layout rather than
	// rendering as a step inside it
	if (!isMaintenance && step === SignInV2Step.FORGOT_PASSWORD) {
		return (
			<ForgotPasswordContainer
				navigation={navigation}
				idNumber={idNumber}
				onBack={onBackToId}
				profile={checkIdResult}
			/>
		);
	}

	return (
		<SignInV2Layout>
			{isMaintenance ? <Maintenance /> : renderStep()}
		</SignInV2Layout>
	);
};

export default SignInV2Container;
